import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { recordWebSearchNudgeAction } from '../../services/api';

// Educational nudge — "Web Search Could Have Helped Here".
// Rendered under an assistant answer when the user had web search OFF and the
// answer hedged about recency. Offers two CTAs: retry this question with search
// on, or switch future messages to Auto. Dismissible, with a "don't show again".
interface WebSearchNudgeProps {
  conversationId?: string;
  // Resend the previous user question with web search forced on (one-shot).
  onRetryWithSearch?: () => void;
  // Switch web search to "auto" for subsequent messages.
  onEnableAuto?: () => void;
}

const WebSearchNudge: React.FC<WebSearchNudgeProps> = ({
  conversationId,
  onRetryWithSearch,
  onEnableAuto,
}) => {
  const { colors } = useTheme();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) {

    return null;
  }

  const handleRetry = () => {
    recordWebSearchNudgeAction('clicked_retry', conversationId);
    setDismissed(true);
    onRetryWithSearch?.();
  };

  const handleAuto = () => {
    recordWebSearchNudgeAction('clicked_auto', conversationId);
    setDismissed(true);
    onEnableAuto?.();
  };

  const handleDismiss = () => {
    recordWebSearchNudgeAction('dismissed', conversationId);
    setDismissed(true);
  };

  const handleDontShowAgain = () => {
    recordWebSearchNudgeAction('dont_show_again', conversationId);
    setDismissed(true);
  };

  return (
    <div style={{
      margin: '12px 0',
      backgroundColor: colors.surface,
      borderRadius: '12px',
      overflow: 'hidden',
      border: `1px solid ${colors.border}`,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    }}>
      <div style={{
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
      }}>
        <span style={{ fontSize: '18px', flexShrink: 0, lineHeight: '1.4' }}>💡</span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '14px', color: colors.textPrimary }}>
            Web search could have helped here
          </div>
          <div style={{ fontSize: '13px', color: colors.textSecondary, marginTop: '2px', lineHeight: '1.5' }}>
            This answer may be limited by the model's knowledge cutoff. Turning on web
            search lets it look up current information.
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
            <button
              onClick={handleRetry}
              style={{
                padding: '6px 12px',
                fontSize: '13px',
                fontWeight: 500,
                backgroundColor: colors.primary,
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span>🔍</span>
              Retry with web search
            </button>
            <button
              onClick={handleAuto}
              style={{
                padding: '6px 12px',
                fontSize: '13px',
                fontWeight: 500,
                backgroundColor: 'transparent',
                color: colors.textPrimary,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Turn on Auto search
            </button>
          </div>
        </div>

        <button
          onClick={handleDismiss}
          title="Dismiss"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: colors.textMuted,
            fontSize: '18px',
            lineHeight: '1',
            padding: '2px 4px',
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>

      <div style={{
        padding: '6px 16px',
        borderTop: `1px solid ${colors.borderLight}`,
        display: 'flex',
        justifyContent: 'flex-end',
        backgroundColor: colors.surfaceElevated,
      }}>
        <button
          onClick={handleDontShowAgain}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: colors.textMuted,
            fontSize: '12px',
            padding: '2px 4px',
          }}
        >
          Don't show again
        </button>
      </div>
    </div>
  );
};

export default WebSearchNudge;
